import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import StorefrontIcon from "@mui/icons-material/Storefront";
import { useEffect, useMemo, useState, type SyntheticEvent } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Stack,
  Tab,
  Tabs,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import { CommonDialog } from "@/components/common/CommonDialog";
import { PrimaryButton, SecondaryButton } from "@/components/common";

import { DismissableMarketplaceOrderCard } from "./DismissableMarketplaceOrderCard";
import { marketplaceCardSx } from "../constants/marketplaceCardStyles";
import { marketplaceTypography as mt } from "../constants/marketplaceTypography";
import type { WooOrder, WooStoreConfig } from "../types";
import { filterOrdersByGroup } from "../utils/orderHelpers";
import {
  countStoresWithOrders,
  groupOrdersByStore,
} from "../utils/storeGroupingHelpers";

type OrdersTab = "unpaid" | "paid" | "cancelled";

function mergeExitingUnpaidOrders(
  unpaid: WooOrder[],
  exiting: WooOrder[],
): WooOrder[] {
  if (exiting.length === 0) return unpaid;

  const merged = [...unpaid];
  for (const order of exiting) {
    if (!merged.some((entry) => entry.id === order.id)) {
      merged.push(order);
    }
  }

  return merged.sort(
    (a, b) =>
      new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime(),
  );
}

interface WooOrdersModalProps {
  open: boolean;
  onClose: () => void;
  initialTab?: OrdersTab;
  orders: WooOrder[];
  stores: WooStoreConfig[];
  loading?: boolean;
  monitoringOrderId?: number | null;
  cancellingOrderId?: number | null;
  onCancelOrder: (order: WooOrder) => void;
  onPayOrder: (order: WooOrder) => void;
  onExploreProducts?: () => void;
}

function StoreAccordionSummary({
  store,
  orderCount,
}: {
  store: WooStoreConfig;
  orderCount: number;
}) {
  const theme = useTheme();

  return (
    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ width: "100%", pr: 0.5 }}>
      {store.logoUrl ? (
        <Box
          component="img"
          src={store.logoUrl}
          alt={store.name}
          sx={{
            width: 36,
            height: 36,
            objectFit: "contain",
            flexShrink: 0,
          }}
        />
      ) : (
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1,
            bgcolor: alpha(theme.palette.primary800.main, 0.1),
            color: "primary800.main",
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
          }}
        >
          <StorefrontIcon sx={{ fontSize: 20 }} />
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0, textAlign: "left" }}>
        <Typography sx={{ ...mt.label, color: "primary800.main" }} noWrap>
          {store.name}
        </Typography>
        <Typography sx={{ ...mt.bodySecondary, color: "text.secondary" }}>
          {orderCount} {orderCount === 1 ? "pedido" : "pedidos"}
        </Typography>
      </Box>
    </Stack>
  );
}

/** Modal dedicado — Meus pedidos (Não pagos | Pagos | Cancelados). */
export function WooOrdersModal({
  open,
  onClose,
  initialTab = "unpaid",
  orders,
  stores,
  loading = false,
  monitoringOrderId = null,
  cancellingOrderId = null,
  onCancelOrder,
  onPayOrder,
  onExploreProducts,
}: WooOrdersModalProps) {
  const theme = useTheme();
  // No mobile/WebView o Alert inline no topo fica fora da vista; usamos sheet de confirmação.
  const isMobileLayout = useMediaQuery(theme.breakpoints.down("md"));
  const [activeTab, setActiveTab] = useState<OrdersTab>(initialTab);
  const [orderToCancel, setOrderToCancel] = useState<WooOrder | null>(null);
  const [exitingOrders, setExitingOrders] = useState<WooOrder[]>([]);
  const [expandedStoreIds, setExpandedStoreIds] = useState<string[]>([]);

  useEffect(() => {
    if (open) setActiveTab(initialTab);
  }, [open, initialTab]);

  useEffect(() => {
    if (!open) {
      setOrderToCancel(null);
      setExitingOrders([]);
      setExpandedStoreIds([]);
    }
  }, [open]);

  useEffect(() => {
    setExitingOrders((prev) =>
      prev.filter((exiting) => {
        const current = orders.find((order) => order.id === exiting.id);
        if (!current) return false;
        return current.status === "cancelled";
      }),
    );
  }, [orders]);

  const unpaidOrders = useMemo(
    () => filterOrdersByGroup(orders, "unpaid"),
    [orders],
  );
  const paidOrders = useMemo(
    () => filterOrdersByGroup(orders, "paid"),
    [orders],
  );
  const cancelledOrders = useMemo(
    () => filterOrdersByGroup(orders, "cancelled"),
    [orders],
  );

  const displayedOrders = useMemo(() => {
    if (activeTab === "unpaid") {
      return mergeExitingUnpaidOrders(unpaidOrders, exitingOrders);
    }
    if (activeTab === "cancelled") return cancelledOrders;
    return paidOrders;
  }, [activeTab, unpaidOrders, paidOrders, cancelledOrders, exitingOrders]);
  const orderGroups = useMemo(
    () => groupOrdersByStore(displayedOrders, stores),
    [displayedOrders, stores],
  );
  const firstStoreId = orderGroups[0]?.store.id ?? null;

  useEffect(() => {
    if (!open) return;

    if (!firstStoreId) {
      setExpandedStoreIds([]);
      return;
    }

    setExpandedStoreIds([]);
    const timer = window.setTimeout(() => {
      setExpandedStoreIds([firstStoreId]);
    }, 160);

    return () => window.clearTimeout(timer);
  }, [open, activeTab, firstStoreId]);

  const storeCount = countStoresWithOrders(orders);
  const subtitle =
    storeCount > 1
      ? `${unpaidOrders.length} aguardando · ${paidOrders.length} pagos · ${cancelledOrders.length} cancelados · ${storeCount} dispensadores`
      : `${unpaidOrders.length} aguardando · ${paidOrders.length} pagos · ${cancelledOrders.length} cancelados`;

  const handleClose = () => {
    if (monitoringOrderId || cancellingOrderId) return;
    onClose();
  };

  const handleConfirmCancel = () => {
    if (!orderToCancel) return;
    const order = orderToCancel;
    setOrderToCancel(null);
    setExitingOrders((prev) =>
      prev.some((entry) => entry.id === order.id) ? prev : [...prev, order],
    );
    onCancelOrder(order);
  };

  const handleDismissComplete = (orderId: number) => {
    setExitingOrders((prev) => prev.filter((order) => order.id !== orderId));
  };

  const handleAccordionChange =
    (storeId: string) => (_: SyntheticEvent, isExpanded: boolean) => {
      setExpandedStoreIds((prev) =>
        isExpanded ? [...prev, storeId] : prev.filter((id) => id !== storeId),
      );
    };

  return (
    <CommonDialog
      isOpen={open}
      handleClose={handleClose}
      maxWidth="md"
      sheetTitle="Meus pedidos"
      sheetSubtitle={subtitle}
      sheetSize="large"
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: { xs: "100%", md: "auto" },
          maxHeight: { xs: "100%", md: "min(560px, 70vh)" },
          minHeight: { md: 360 },
          overflow: "hidden",
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, value: OrdersTab) => {
            setActiveTab(value);
          }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            mb: 1.5,
            minHeight: 40,
            borderBottom: 1,
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <Tab
            label={`Não pagos (${unpaidOrders.length})`}
            value="unpaid"
            sx={{ minHeight: 40, ...mt.bodySecondary }}
          />
          <Tab
            label={`Pagos (${paidOrders.length})`}
            value="paid"
            sx={{ minHeight: 40, ...mt.bodySecondary }}
          />
          <Tab
            label={`Cancelados (${cancelledOrders.length})`}
            value="cancelled"
            sx={{ minHeight: 40, ...mt.bodySecondary }}
          />
        </Tabs>

        {monitoringOrderId ? (
          <Alert severity="info" sx={{ mb: 1.5, ...mt.bodySecondary, flexShrink: 0 }}>
            Aguardando confirmação do pagamento…
          </Alert>
        ) : null}

        {/* Desktop: confirmação compacta no topo. Mobile usa CommonDialog abaixo. */}
        {orderToCancel && !isMobileLayout ? (
          <Alert
            severity="warning"
            sx={{ mb: 1.5, ...mt.bodySecondary, flexShrink: 0 }}
            action={
              <Stack direction="row" spacing={0.5} flexShrink={0}>
                <Button
                  size="small"
                  color="inherit"
                  onClick={() => setOrderToCancel(null)}
                  disabled={Boolean(cancellingOrderId)}
                >
                  Voltar
                </Button>
                <Button
                  size="small"
                  color="warning"
                  variant="contained"
                  onClick={handleConfirmCancel}
                  disabled={Boolean(cancellingOrderId)}
                >
                  Confirmar
                </Button>
              </Stack>
            }
          >
            Cancelar o pedido #{orderToCancel.id}? Esta ação não pode ser desfeita.
          </Alert>
        ) : null}

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            px: { xs: 0, md: 0.5 },
            pb: 0.5,
            WebkitOverflowScrolling: "touch",
          }}
        >
        {loading && orders.length === 0 ? (
          <Typography sx={{ ...mt.body, color: "text.secondary", py: 3, textAlign: "center" }}>
            Carregando pedidos…
          </Typography>
        ) : displayedOrders.length === 0 ? (
          <Stack spacing={1.5} alignItems="center" sx={{ py: 4, textAlign: "center" }}>
            <Typography sx={{ ...mt.body, color: "text.secondary" }}>
              {activeTab === "unpaid"
                ? "Nenhum pedido aguardando pagamento."
                : activeTab === "cancelled"
                  ? "Nenhum pedido cancelado."
                  : "Nenhum pedido pago ainda."}
            </Typography>
            {onExploreProducts ? (
              <SecondaryButton width="fit-content" height="44px" onClick={onExploreProducts}>
                Explorar produtos
              </SecondaryButton>
            ) : null}
          </Stack>
        ) : (
          <Stack spacing={1.5}>
            {orderGroups.map(({ store, orders: storeOrders }) => (
              <Accordion
                key={store.id}
                expanded={expandedStoreIds.includes(store.id)}
                onChange={handleAccordionChange(store.id)}
                disableGutters
                elevation={0}
                sx={{
                  ...marketplaceCardSx(theme),
                  "&:before": { display: "none" },
                  overflow: "hidden",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon color="primary" />}
                  aria-controls={`orders-store-${store.id}`}
                  id={`orders-store-header-${store.id}`}
                  sx={{
                    minHeight: 56,
                    px: 1.5,
                    "& .MuiAccordionSummary-content": { my: 1 },
                  }}
                >
                  <StoreAccordionSummary store={store} orderCount={storeOrders.length} />
                </AccordionSummary>
                <AccordionDetails
                  id={`orders-store-${store.id}`}
                  sx={{ px: 1.5, pt: 0, pb: 1.5 }}
                >
                  <Stack spacing={1.5}>
                    {storeOrders.map((order) => {
                      const isDismissing = exitingOrders.some(
                        (entry) => entry.id === order.id,
                      );

                      return (
                        <DismissableMarketplaceOrderCard
                          key={order.id}
                          order={order}
                          showPendingActions={activeTab === "unpaid" && !isDismissing}
                          monitoringOrderId={monitoringOrderId}
                          cancelling={cancellingOrderId === order.id}
                          disabled={Boolean(
                            cancellingOrderId && cancellingOrderId !== order.id,
                          )}
                          isDismissing={isDismissing}
                          onDismissComplete={() => handleDismissComplete(order.id)}
                          onPay={
                            activeTab === "unpaid" && !isDismissing
                              ? () => onPayOrder(order)
                              : undefined
                          }
                          onCancel={
                            activeTab === "unpaid" && !isDismissing
                              ? () => setOrderToCancel(order)
                              : undefined
                          }
                        />
                      );
                    })}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        )}
        </Box>
      </Box>

      {/*
        Mobile: sheet dedicado acima de "Meus pedidos".
        O Alert do topo fica escondido no scroll do bottom sheet / WebView.
      */}
      {isMobileLayout ? (
        <CommonDialog
          isOpen={Boolean(orderToCancel)}
          handleClose={() => {
            if (cancellingOrderId) return;
            setOrderToCancel(null);
          }}
          maxWidth="xs"
          sheetTitle="Cancelar pedido?"
          sheetSubtitle={
            orderToCancel
              ? `Pedido #${orderToCancel.id}`
              : undefined
          }
          sheetSize="auto"
          // Acima do sheet "Meus pedidos" (default zIndexOffset = 20)
          sheetZIndexOffset={40}
          footer={
            <Stack
              direction="column-reverse"
              spacing={1}
              width="100%"
            >
              <SecondaryButton
                width="100%"
                height="48px"
                onClick={() => setOrderToCancel(null)}
                disabled={Boolean(cancellingOrderId)}
              >
                Voltar
              </SecondaryButton>
              <PrimaryButton
                width="100%"
                height="48px"
                onClick={handleConfirmCancel}
                loading={Boolean(cancellingOrderId)}
                disabled={Boolean(cancellingOrderId)}
              >
                Confirmar cancelamento
              </PrimaryButton>
            </Stack>
          }
        >
          <Typography
            sx={{
              ...mt.body,
              color: "text.secondary",
              px: { xs: 0, md: 0.5 },
              pb: 0.5,
            }}
          >
            Esta ação não pode ser desfeita. O pedido voltará para a aba
            Cancelados.
          </Typography>
        </CommonDialog>
      ) : null}
    </CommonDialog>
  );
}

export type { OrdersTab };
